import { formatDistanceToNow } from 'date-fns'
import { X } from 'lucide-react'
import type { NotificationItem } from '@/context/NotificationContext'

interface NotificationRowProps {
  notification: NotificationItem
  /** The row was clicked: go to the record. */
  onOpen: () => void
  /** The x was clicked: hide this notification. */
  onDelete: () => void
}

// One notification in the bell panel. An unread one has a coloured edge, a tinted
// background, a dot and a bold title; a read one is plain and muted.
export function NotificationRow({ notification, onOpen, onDelete }: NotificationRowProps) {
  const unread = !notification.isRead
  return (
    <div
      role="button"
      tabIndex={0}
      data-unread={unread}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      className={`group flex items-start gap-2 border-b border-border border-l-4 p-3 last:border-b-0 cursor-pointer transition-colors ${
        unread ? 'border-l-primary bg-primary/10 hover:bg-primary/15' : 'border-l-transparent hover:bg-muted/50'
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          {unread && <span aria-label="Unread" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
          <p className={`text-sm ${unread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'}`}>{notification.title}</p>
        </div>
        {notification.message && (
          <p className={`mt-1 line-clamp-2 text-xs ${unread ? 'text-foreground/70' : 'text-muted-foreground'}`}>{notification.message}</p>
        )}
        <p className="mt-1 text-[10px] text-muted-foreground">
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
        </p>
      </div>
      <button
        type="button"
        aria-label="Delete notification"
        title="Delete"
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        className="shrink-0 rounded p-1 text-muted-foreground opacity-60 transition hover:bg-muted hover:text-foreground group-hover:opacity-100 focus:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
